import { SxProps, Theme } from '@mui/material';
import Box from '@mui/material/Box';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import { useMemo } from 'react';

import { OverflowAwareText } from '~components/Order/ordersDispatchStyledComponents';

interface OrderProgressWidgetProps {
  count: number;
  primaryText: React.ReactNode;
  secondaryText?: React.ReactNode;
  sx?: SxProps<Theme>;
  label?: string;
  total: number;
}

export default function OrderProgressWidget({
  count,
  primaryText,
  secondaryText,
  sx,
  total,
  label,
}: OrderProgressWidgetProps) {
  const isStringPrimaryText = typeof primaryText === 'string';
  const isStringSecondaryText = typeof secondaryText === 'string';

  const progressValue = useMemo(() => {
    const value = (count * 100) / (total || 1);

    if (value <= 0) {
      return 0;
    }

    if (value >= 100) {
      return 100;
    }

    return value;
  }, [count, total]);

  return (
    <Box
      sx={[
        {
          alignItems: 'flex-start',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          minWidth: '72px',
          width: '100%',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {label && (
        <OverflowAwareText
          sx={(theme) => ({
            fontSize: '8px',
            fontWeight: 400,
            color: theme.brandV2.colors.treadGray1,
          })}
        >
          {label.toUpperCase()}
        </OverflowAwareText>
      )}
      {isStringPrimaryText ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
          <OverflowAwareText
            sx={(theme) => ({
              fontSize: '14px',
              fontWeight: 600,
              color: theme.brandV2.colors.treadGray1,
            })}
          >
            {primaryText}
          </OverflowAwareText>
        </Box>
      ) : (
        primaryText
      )}

      <LinearProgress
        variant="determinate"
        value={progressValue}
        sx={[
          (theme) => ({
            [`&.${linearProgressClasses.root}`]: {
              bgcolor: theme.brandV2.colors.treadGray7,
              borderRadius: 1.2,
              height: '4px',
              maxWidth: '72px',
              width: '100%',
            },
            [`& .${linearProgressClasses.bar}`]: {
              bgcolor: theme.brandV2.colors.treadBlue,
            },
          }),
        ]}
      />

      {isStringSecondaryText ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
          <OverflowAwareText
            sx={(theme) => ({
              color: theme.brandV2.colors.treadBlack,
              fontSize: '12px',
              fontWeight: 400,
            })}
          >
            {secondaryText}
          </OverflowAwareText>
        </Box>
      ) : (
        secondaryText
      )}
    </Box>
  );
}
